import { useEffect, useState, useCallback } from "react";
import AdminHeader from "../components/admin/AdminHeader";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Sparkles, Eye, EyeOff, Trash2, ExternalLink, Loader2, RefreshCw,
  Plus, X, FlaskConical,
} from "lucide-react";
import {
  adminListBlog, adminGenerateBlog, adminBlogStatus,
  adminPublishBlog, adminUnpublishBlog, adminDeleteBlog, blogImageUrl,
  adminGetBlogSettings, adminSaveBlogSettings,
  adminCreatePost, adminUpdatePost, adminGetPost,
  adminGetResearchBrief, adminStartResearch,
} from "../lib/blogApi";
import ResearchBriefPanel from "../components/ResearchBriefPanel";

const SOURCE_TYPES = [
  "FDA/DailyMed", "PubMed", "Professional medical organisation",
  "Product label", "Other reliable source",
];
const STATUS_LABELS = {
  discovered: "Discovered", selected: "Selected", researching: "Researching",
  writing: "Writing", evidence_review: "Evidence Review", seo_review: "SEO Review",
  ready_to_publish: "Ready to Publish", published: "Published",
  needs_update: "Needs Update", rejected: "Rejected", draft: "Draft",
};

function BlogEditor({ postId, onClose, onSaved }) {
  const empty = {
    title: "", slug: "", body_markdown: "", author_name: "Meghana", status: "draft",
    category: "medication_guide", excerpt: "", linked_pair_urls: "",
  };
  const [f, setF] = useState(empty);
  const [sources, setSources] = useState([]);
  const [busy, setBusy] = useState(false);
  const [brief, setBrief] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [mobileTab, setMobileTab] = useState("brief");

  useEffect(() => {
    if (postId) {
      adminGetPost(postId).then((d) => {
        setF({
          title: d.title || "", slug: d.slug || "", body_markdown: d.body_markdown || "",
          author_name: d.author || "Meghana", status: d.status || "draft",
          category: d.category || "medication_guide", excerpt: d.excerpt || "",
          linked_pair_urls: (d.linked_pair_urls || []).join("\n"),
        });
        const existing = d.sources && d.sources.length
          ? d.sources
          : (d.source_urls || []).map((u) => ({ title: "", url: u, type: "Other reliable source" }));
        setSources(existing);
      }).catch(() => toast.error("Failed to load post"));

      setBriefLoading(true);
      adminGetResearchBrief(postId)
        .then(setBrief)
        .catch(() => setBrief({ linked: false }))
        .finally(() => setBriefLoading(false));
    } else {
      setF(empty);
      setSources([]);
      setBrief({ linked: false });
    }
  }, [postId]);

  const addSource = () => setSources((s) => [...s, { title: "", url: "", type: SOURCE_TYPES[0] }]);
  const updateSource = (i, key, val) => setSources((s) => s.map((row, idx) => idx === i ? { ...row, [key]: val } : row));
  const removeSource = (i) => setSources((s) => s.filter((_, idx) => idx !== i));

  const startResearch = async () => {
    const oppId = brief?.seo_opportunity_id;
    if (!oppId) return;
    try {
      await adminStartResearch(oppId);
      toast.success("Marked as Researching");
      const fresh = await adminGetResearchBrief(postId);
      setBrief(fresh);
    } catch { toast.error("Could not start research"); }
  };

  const save = async () => {
    if (!f.title.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    const cleanSources = sources.filter((s) => (s.url || "").trim());
    const payload = {
      ...f,
      linked_pair_urls: f.linked_pair_urls.split("\n").map((s) => s.trim()).filter(Boolean),
      sources: cleanSources,
      source_urls: cleanSources.map((s) => s.url.trim()),
    };
    try {
      if (postId) await adminUpdatePost(postId, payload);
      else await adminCreatePost(payload);
      toast.success("Saved");
      onSaved();
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.publish_blocked) toast.error(`Cannot publish: ${d.failures[0]}`);
      else toast.error("Save failed");
    } finally { setBusy(false); }
  };

  const isSeo = brief && brief.linked !== false;
  const canStartResearch = isSeo && (brief.article_status === "selected" || brief.article_status === "discovered");

  const editorForm = (
    <div className="space-y-3" data-testid="blog-editor-form">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 className="text-[13px] font-bold uppercase tracking-wide text-ink">Write Article</h3>
          {isSeo && brief.article_status && (
            <span data-testid="blog-editor-status-badge" className="rounded-full bg-cream-100 px-2.5 py-0.5 text-[11px] font-semibold text-ink-soft">
              {STATUS_LABELS[brief.article_status] || brief.article_status}
            </span>
          )}
        </div>
        {canStartResearch && (
          <button data-testid="blog-editor-start-research" onClick={startResearch}
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-[#1f3d2e] text-[#1f3d2e] text-[12px] font-bold hover:bg-[#1f3d2e] hover:text-cream-50 transition-all">
            <FlaskConical className="h-3.5 w-3.5" /> Start Research
          </button>
        )}
      </div>
      <input data-testid="blog-editor-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Title" className="w-full h-10 rounded-lg border border-surface-line px-3 text-sm" />
      <input data-testid="blog-editor-slug" value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} placeholder="Slug (auto from title if blank)" className="w-full h-10 rounded-lg border border-surface-line px-3 text-sm" />
      <input data-testid="blog-editor-excerpt" value={f.excerpt} onChange={(e) => setF({ ...f, excerpt: e.target.value })} placeholder="Excerpt (write manually)" className="w-full h-10 rounded-lg border border-surface-line px-3 text-sm" />
      <textarea data-testid="blog-editor-body" value={f.body_markdown} onChange={(e) => setF({ ...f, body_markdown: e.target.value })} rows={14} placeholder="Write the article here (Markdown). Left blank on purpose — nothing is generated." className="w-full rounded-lg border border-surface-line p-3 font-mono text-[13px]" />

      <div>
        <div className="flex items-center justify-between">
          <span className="text-[12px] font-semibold text-ink-soft">Sources</span>
          <button data-testid="blog-editor-add-source" onClick={addSource} className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#1f3d2e]">
            <Plus className="h-3.5 w-3.5" /> Add another source
          </button>
        </div>
        <div className="mt-2 space-y-2">
          {sources.length === 0 && <p className="text-[12px] text-ink-mute">No sources yet. Add reliable sources before evidence review.</p>}
          {sources.map((src, i) => (
            <div key={i} data-testid={`blog-editor-source-${i}`} className="flex flex-wrap items-center gap-2">
              <input data-testid={`blog-editor-source-title-${i}`} value={src.title} onChange={(e) => updateSource(i, "title", e.target.value)} placeholder="Source title" className="h-9 flex-1 min-w-[120px] rounded-lg border border-surface-line px-2.5 text-[13px]" />
              <input data-testid={`blog-editor-source-url-${i}`} value={src.url} onChange={(e) => updateSource(i, "url", e.target.value)} placeholder="https://…" className="h-9 flex-1 min-w-[140px] rounded-lg border border-surface-line px-2.5 text-[13px]" />
              <select data-testid={`blog-editor-source-type-${i}`} value={src.type} onChange={(e) => updateSource(i, "type", e.target.value)} className="h-9 rounded-lg border border-surface-line px-2 text-[12px]">
                {SOURCE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <button data-testid={`blog-editor-source-remove-${i}`} onClick={() => removeSource(i)} className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-surface-line text-red-500 hover:bg-red-50">
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <input data-testid="blog-editor-author" value={f.author_name} onChange={(e) => setF({ ...f, author_name: e.target.value })} placeholder="Author" className="h-10 rounded-lg border border-surface-line px-3 text-sm" />
        <select data-testid="blog-editor-status" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="h-10 rounded-lg border border-surface-line px-3 text-sm">
          <option value="draft">Draft</option>
          <option value="published">Published</option>
        </select>
      </div>
      <textarea data-testid="blog-editor-pairs" value={f.linked_pair_urls} onChange={(e) => setF({ ...f, linked_pair_urls: e.target.value })} rows={2} placeholder="Internal-link URLs (one per line)" className="w-full rounded-lg border border-surface-line p-3 text-[13px]" />
    </div>
  );

  const briefPane = (
    <ResearchBriefPanel
      brief={brief}
      loading={briefLoading}
      articleId={postId}
      onPaaUpdated={(paa) => setBrief((b) => (b ? { ...b, paa } : b))}
    />
  );

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-3 overflow-y-auto" data-testid="blog-editor-modal">
      <div className="mt-4 w-full max-w-6xl rounded-2xl bg-cream-50 p-5 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-ink">{postId ? "Edit post" : "New post"}</h2>
          <button data-testid="blog-editor-cancel" onClick={onClose} className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-surface-line text-ink-mute hover:text-ink"><X className="h-4 w-4" /></button>
        </div>

        {/* Mobile tabs */}
        <div className="mt-4 flex gap-1.5 border-b border-surface-line md:hidden">
          <button data-testid="blog-editor-tab-brief" onClick={() => setMobileTab("brief")} className={`px-3 py-2 text-[13px] font-semibold ${mobileTab === "brief" ? "border-b-2 border-ink text-ink" : "text-ink-mute"}`}>Research Brief</button>
          <button data-testid="blog-editor-tab-write" onClick={() => setMobileTab("write")} className={`px-3 py-2 text-[13px] font-semibold ${mobileTab === "write" ? "border-b-2 border-ink text-ink" : "text-ink-mute"}`}>Write Article</button>
        </div>

        {/* Desktop two-column: brief ~38% / editor ~62% */}
        <div className="mt-4 hidden gap-5 md:flex" data-testid="blog-editor-desktop">
          <div className="w-[38%] max-h-[72vh] overflow-y-auto pr-1">{briefPane}</div>
          <div className="w-[62%] max-h-[72vh] overflow-y-auto rounded-2xl bg-white p-4">{editorForm}</div>
        </div>

        {/* Mobile single-pane */}
        <div className="mt-4 md:hidden">
          {mobileTab === "brief" ? briefPane : <div className="rounded-2xl bg-white p-4">{editorForm}</div>}
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 rounded-lg border border-surface-line text-sm font-semibold">Cancel</button>
          <button data-testid="blog-editor-save" onClick={save} disabled={busy} className="h-10 px-5 rounded-lg bg-ink text-cream-50 text-sm font-bold disabled:opacity-60">Save</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminBlog() {
  const [data, setData] = useState({ posts: [], published_count: 0, draft_count: 0 });
  const [loading, setLoading] = useState(true);
  const [genCount, setGenCount] = useState(3);
  const [job, setJob] = useState({ running: false, last: null });
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    adminListBlog("all")
      .then(setData)
      .catch(() => toast.error("Failed to load posts"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);
  // Auto-open the editor when arriving from "Select for Article" (/admin/blog?edit=<id>).
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("edit");
    if (id) { setEditId(id); setEditorOpen(true); }
  }, []);
  useEffect(() => {
    adminGetBlogSettings().then(setSettings).catch(() => {});
  }, []);

  const toggleCat = (key) => {
    setSettings((s) => {
      const has = s.categories.includes(key);
      const next = has ? s.categories.filter((c) => c !== key) : [...s.categories, key];
      return { ...s, categories: next.length ? next : s.categories };
    });
  };

  const saveSettings = async () => {
    if (!settings) return;
    setSavingSettings(true);
    try {
      const saved = await adminSaveBlogSettings(settings.posts_per_week, settings.categories);
      setSettings((s) => ({ ...s, ...saved }));
      toast.success("Cadence settings saved");
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  // Poll job status while a generation run is active.
  useEffect(() => {
    let t;
    const tick = async () => {
      try {
        const s = await adminBlogStatus();
        setJob(s);
        if (s.running) {
          t = setTimeout(tick, 4000);
        } else {
          load();
        }
      } catch { /* ignore */ }
    };
    if (job.running) t = setTimeout(tick, 4000);
    return () => clearTimeout(t);
  }, [job.running, load]);

  const generate = async () => {
    try {
      const r = await adminGenerateBlog(genCount);
      if (r.running && !r.ok) {
        toast.info("A generation job is already running.");
      } else {
        toast.success(`Generating ${genCount} article(s) in the background…`);
      }
      setJob({ running: true, last: job.last });
    } catch {
      toast.error("Failed to start generation");
    }
  };

  const doAction = async (fn, id, okMsg) => {
    try {
      await fn(id);
      toast.success(okMsg);
      load();
    } catch {
      toast.error("Action failed");
    }
  };

  const del = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    doAction(adminDeleteBlog, id, "Post deleted");
  };

  return (
    <main className="bg-cream-50 min-h-screen" data-testid="admin-blog-page">
      <div className="max-w-screen-lg mx-auto px-5 sm:px-8 py-8">
        <AdminHeader />

        <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-[-0.02em] text-ink">Blog</h1>
            <p className="mt-1 text-[13px] text-ink-mute">
              {data.published_count} published · {data.draft_count} drafts · auto-publishes ~3/week
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-blog-new-post"
              onClick={() => { setEditId(null); setEditorOpen(true); }}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-ink text-ink text-[13px] font-bold hover:bg-ink hover:text-cream-50 transition-all"
            >
              + New post
            </button>
            <select
              data-testid="admin-blog-gen-count"
              value={genCount}
              onChange={(e) => setGenCount(Number(e.target.value))}
              className="h-10 rounded-lg border border-surface-line bg-white px-3 text-[13px] font-medium text-ink"
            >
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <option key={n} value={n}>{`${n} article${n > 1 ? "s" : ""}`}</option>
              ))}
            </select>
            <button
              data-testid="admin-blog-generate"
              onClick={generate}
              disabled={job.running}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-ink text-cream-50 text-[13px] font-bold hover:bg-ink-soft disabled:opacity-60 transition-all"
            >
              {job.running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {job.running ? "Generating…" : "Generate now"}
            </button>
            <button
              data-testid="admin-blog-refresh"
              onClick={load}
              className="inline-flex items-center justify-center h-10 w-10 rounded-lg border border-surface-line bg-white text-ink-mute hover:text-ink"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {job.last && !job.running && (
          <div className="mt-4 text-[12.5px] text-ink-mute" data-testid="admin-blog-last-job">
            Last run: {job.last.created?.length || 0} created
            {job.last.errors?.length ? `, ${job.last.errors.length} error(s)` : ""}
            {job.last.no_topics_left ? " — no fresh topics left" : ""}
          </div>
        )}

        {/* Cadence controls */}
        {settings && (
          <div
            className="mt-6 rounded-xl border border-surface-line bg-white p-5"
            data-testid="admin-blog-cadence"
          >
            <p className="text-[14px] font-bold text-ink">Publishing cadence</p>
            <p className="text-[12.5px] text-ink-mute mt-0.5">
              How many articles the scheduler auto-publishes each week, and which formats it picks from.
            </p>
            <div className="mt-4 flex flex-wrap items-end gap-6">
              <label className="block">
                <span className="text-[12px] font-semibold text-ink-soft">Posts per week</span>
                <select
                  data-testid="admin-blog-ppw"
                  value={settings.posts_per_week}
                  onChange={(e) => setSettings((s) => ({ ...s, posts_per_week: Number(e.target.value) }))}
                  className="mt-1.5 block h-10 rounded-lg border border-surface-line bg-cream-50 px-3 text-[13px] font-medium text-ink"
                >
                  {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                    <option key={n} value={n}>{`${n} / week`}</option>
                  ))}
                </select>
              </label>
              <div>
                <span className="text-[12px] font-semibold text-ink-soft">Categories</span>
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {(settings.all_categories || []).map((c) => {
                    const on = settings.categories.includes(c.key);
                    return (
                      <button
                        key={c.key}
                        data-testid={`admin-blog-cat-${c.key}`}
                        onClick={() => toggleCat(c.key)}
                        className={`h-9 px-3.5 rounded-full text-[12.5px] font-semibold transition-all ${
                          on ? "bg-ink text-cream-50" : "bg-cream-50 border border-surface-line text-ink-soft hover:border-ink/30"
                        }`}
                      >
                        {c.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <button
                data-testid="admin-blog-save-settings"
                onClick={saveSettings}
                disabled={savingSettings}
                className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-[#1f3d2e] text-cream-50 text-[13px] font-bold hover:opacity-90 disabled:opacity-60 transition-all"
              >
                {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Save cadence
              </button>
            </div>
          </div>
        )}

        <div className="mt-6 space-y-3">
          {loading ? (
            <p className="text-ink-mute text-[14px]">Loading…</p>
          ) : data.posts.length === 0 ? (
            <p className="text-ink-mute text-[14px]" data-testid="admin-blog-empty">
              No posts yet. Click “Generate now” to create the first batch.
            </p>
          ) : (
            data.posts.map((p) => {
              const cover = blogImageUrl(p.cover_image_url);
              return (
                <div
                  key={p.id}
                  data-testid={`admin-blog-row-${p.slug}`}
                  className="flex items-center gap-4 rounded-xl border border-surface-line bg-white p-3"
                >
                  <div className="w-16 h-12 rounded-lg bg-cream-100 overflow-hidden shrink-0">
                    {cover && <img src={cover} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[14px] font-semibold text-ink truncate">{p.title}</p>
                    <p className="text-[11.5px] text-ink-mute">
                      {p.category_label} · {p.reading_minutes} min ·{" "}
                      <span className={p.status === "published" ? "text-[#1f8f4e] font-semibold" : "text-[#a87a32] font-semibold"}>
                        {p.status}
                      </span>
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <a
                      href={`/blog/${p.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`admin-blog-view-${p.slug}`}
                      className="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-surface-line text-ink-mute hover:text-ink"
                      title="View"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                    <button
                      data-testid={`admin-blog-edit-${p.slug}`}
                      onClick={() => { setEditId(p.id); setEditorOpen(true); }}
                      className="inline-flex items-center justify-center h-9 px-3 rounded-lg border border-surface-line text-ink-mute hover:text-ink text-[12px] font-semibold"
                      title="Edit"
                    >
                      Edit
                    </button>
                    {p.status === "published" ? (
                      <button
                        data-testid={`admin-blog-unpublish-${p.slug}`}
                        onClick={() => doAction(adminUnpublishBlog, p.id, "Unpublished")}
                        className="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-surface-line text-ink-mute hover:text-ink"
                        title="Unpublish (kill switch)"
                      >
                        <EyeOff className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        data-testid={`admin-blog-publish-${p.slug}`}
                        onClick={() => doAction(adminPublishBlog, p.id, "Published")}
                        className="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-surface-line text-ink-mute hover:text-ink"
                        title="Publish"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      data-testid={`admin-blog-delete-${p.slug}`}
                      onClick={() => del(p.id, p.title)}
                      className="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-surface-line text-red-500 hover:bg-red-50"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
      {editorOpen && (
        <BlogEditor
          postId={editId}
          onClose={() => setEditorOpen(false)}
          onSaved={load}
        />
      )}
    </main>
  );
}
